import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";

const SettingsContext = React.createContext<{
  maxWidth?: string;
}>({});

const SettingsCard: React.FC<
  React.PropsWithChildren<{
    className?: string;
    title?: string;
    description?: string;
    actions?: React.ReactNode;
  }>
> = ({ children, className, title, description, actions }) => {
  const { maxWidth } = React.useContext(SettingsContext);

  return (
    <div
      className={twMerge(
        "border-0.5 border-border flex flex-col gap-6 rounded-xl p-5 shadow-sm bg-white h-fit w-full",
        maxWidth ?? "max-w-[650px]",
        className,
      )}
    >
      {(title ?? description ?? actions) && (
        <div className="flex flex-row items-center justify-between space-y-0 p-0">
          {(title ?? description) && (
            <div className="flex flex-col items-start">
              {title && <p className="header-h5 break-words">{title}</p>}
              {description && (
                <p className="text-sm text-muted break-words leading-relaxed">
                  {description}
                </p>
              )}
            </div>
          )}
          {actions && <div className="ml-auto">{actions}</div>}
        </div>
      )}
      <div className="flex flex-col gap-6 p-0">{children}</div>
    </div>
  );
};

type SettingsContentProps = {
  title?: string;
};

const SettingsContent: React.FC<
  React.PropsWithChildren<SettingsContentProps>
> = ({ children, title }) => {
  return (
    <div className="flex flex-col gap-3 w-full">
      {title && (
        <span className="text-sm font-medium break-words">{title}</span>
      )}
      {children}
    </div>
  );
};

export const SettingsLayout: React.FC<
  React.PropsWithChildren<{
    maxWidth?: string;
  }>
> = ({ children, maxWidth }) => {
  return (
    <SettingsContext.Provider value={{ maxWidth }}>
      <div className="flex flex-col gap-4 flex-1 items-center mb-4">
        {children}
      </div>
    </SettingsContext.Provider>
  );
};

type LoadedSettingsProps = {
  sections: {
    title?: string;
    description?: string;
    actions?: React.ReactNode;
    component: React.ReactNode;
    componentTitle?: string;
  }[];
};

export const LoadedSettings = ({ sections }: LoadedSettingsProps) => {
  return sections.map((section) => {
    return (
      <SettingsCard
        key={section.title}
        title={section.title}
        description={section.description}
        actions={section.actions}
      >
        <SettingsContent title={section.componentTitle}>
          {section.component}
        </SettingsContent>
      </SettingsCard>
    );
  });
};
